import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { ProfileFormData } from "@/types/profile";

export const useProfile = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    loadProfile().then(setProfile);
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;

      // First get the profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();

      if (!profile) return null;

      // Then get the admin status separately
      const { data: adminData } = await supabase
        .from("admin_users")
        .select("can_delete_accounts, can_moderate_marketplace")
        .eq("id", session.user.id)
        .maybeSingle();

      // Combine the data
      return {
        ...profile,
        admin_user: adminData
      };

    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load profile. Please try again.",
      });
      return null;
    }
  };

  const updateProfile = async (data: ProfileFormData) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("No user session found");
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: data.full_name,
          organization_name: data.organization_name,
          role: data.role,
          updated_at: new Date().toISOString(),
        })
        .eq("id", session.user.id);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
      
      const updatedProfile = await loadProfile();
      setProfile(updatedProfile);
      return true;
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update profile. Please try again.",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    profile,
    loadProfile,
    updateProfile,
  };
};